/**
 * FAPI JWT proactive refresh.
 *
 * Schedules a timer to refresh the token 5 minutes before expiry so Web-STOMP,
 * presence WebSocket, and other JWT consumers never hit an expired token.
 *
 * Call scheduleProactiveJwtRefresh() whenever the token is updated (page load,
 * after fetch). Runs automatically on module load if __FAPI_JWT_EXPIRES is set.
 */
/* eslint-env es2022 */


!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{},n=(new e.Error).stack;n&&(e._sentryDebugIds=e._sentryDebugIds||{},e._sentryDebugIds[n]="19488bbd-760b-5230-a182-79e86dd21278")}catch(e){}}();
const REFRESH_BEFORE_EXPIRY_SEC = 300;  // 5 minutes
const FAPI_TOKEN_URL = '/tools/api/auth/fapi-token/';
const RETRY_BASE_DELAY_MS = 30_000;
const RETRY_MAX_DELAY_MS = 5 * 60_000;
const WARNING_THROTTLE_MS = 60_000;

let refreshTimerId = null;
let inFlightRefreshPromise = null;
/** Dedupe parallel refreshes only when the same conversation scope is requested. */
let inFlightRefreshKey = '';
let consecutiveRefreshFailures = 0;
let nextRetryNotBeforeMs = 0;
let lastWarningAtMs = 0;

function getCurrentTokenIfUsable() {
    if (typeof window === 'undefined') return null;
    const token = window.__FAPI_JWT || null;
    const expiresAt = window.__FAPI_JWT_EXPIRES || 0;
    if (!token || !expiresAt) return null;
    const now = Math.floor(Date.now() / 1000);
    if (expiresAt <= now) return null;
    return token;
}

function warnTokenRefreshFailure(msg, extra) {
    const nowMs = Date.now();
    if (nowMs - lastWarningAtMs < WARNING_THROTTLE_MS) return;
    lastWarningAtMs = nowMs;
    console.warn(msg, extra);
}

function resetFailureState() {
    consecutiveRefreshFailures = 0;
    nextRetryNotBeforeMs = 0;
}

function scheduleRetryAfterFailure() {
    const exponent = Math.max(0, consecutiveRefreshFailures - 1);
    const backoff = Math.min(RETRY_MAX_DELAY_MS, RETRY_BASE_DELAY_MS * (2 ** exponent));
    nextRetryNotBeforeMs = Date.now() + backoff;

    if (refreshTimerId !== null) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
    }
    refreshTimerId = setTimeout(() => {
        refreshTimerId = null;
        fetchAndUpdateFapiToken();
    }, backoff);
}

async function parseTokenResponse(res) {
    const contentType = (res.headers.get('content-type') || '').toLowerCase();
    if (contentType.includes('application/json')) {
        return res.json();
    }

    const bodyText = await res.text();
    const bodyPreview = bodyText ? bodyText.slice(0, 200) : '';
    throw new Error(`Unexpected content-type from token endpoint (${contentType || 'unknown'}): ${bodyPreview}`);
}

/**
 * Fetch JWT from Django, update globals, and schedule next proactive refresh.
 * @param {Object} [opts]
 * @param {string} [opts.conversationId] - Optional; also reads window.__FAPI_TOKEN_CONVERSATION_ID when omitted.
 * @returns {Promise<string|null>} The new token or null on failure
 */
export async function fetchAndUpdateFapiToken(opts = {}) {
    const explicitCid = String(opts.conversationId || '').trim();
    const windowCid = typeof window !== 'undefined'
        ? String(window.__FAPI_TOKEN_CONVERSATION_ID || '').trim()
        : '';
    const conversationId = explicitCid || windowCid;
    const refreshKey = conversationId || '__default__';

    if (inFlightRefreshPromise !== null) {
        if (inFlightRefreshKey === refreshKey) {
            return inFlightRefreshPromise;
        }
        await inFlightRefreshPromise;
    }

    if (Date.now() < nextRetryNotBeforeMs) {
        return getCurrentTokenIfUsable();
    }

    inFlightRefreshKey = refreshKey;
    inFlightRefreshPromise = (async () => {
        try {
            const tokenUrl = conversationId
                ? `${FAPI_TOKEN_URL}?conversation_id=${encodeURIComponent(conversationId)}`
                : FAPI_TOKEN_URL;
            const res = await fetch(tokenUrl, {
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            const data = await parseTokenResponse(res);
            if (res.ok && data?.valid && data?.token) {
                if (typeof window !== 'undefined') {
                    window.__FAPI_JWT = data.token;
                    window.__FAPI_JWT_EXPIRES = data.expires_at || 0;
                }
                resetFailureState();
                scheduleProactiveJwtRefresh();
                return data.token;
            }

            consecutiveRefreshFailures += 1;
            scheduleRetryAfterFailure();
            warnTokenRefreshFailure('FAPI token: refresh returned invalid payload/status', {
                status: res.status,
                valid: !!data?.valid,
                hasToken: !!data?.token,
            });
        } catch (e) {
            consecutiveRefreshFailures += 1;
            scheduleRetryAfterFailure();
            warnTokenRefreshFailure('FAPI token: failed to refresh', e);
            return getCurrentTokenIfUsable();
        }
        return null;
    })();

    try {
        return await inFlightRefreshPromise;
    } finally {
        inFlightRefreshPromise = null;
        inFlightRefreshKey = '';
    }
}

/**
 * Return a FAPI JWT that is valid for at least 5 more minutes, refreshing when needed.
 * Preferred accessor for callers attaching Authorization headers to FAPI requests.
 * @returns {Promise<string|null>} Token string, or null when no token could be obtained
 */
export async function getFapiToken() {
    if (typeof window === 'undefined') return null;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = window.__FAPI_JWT_EXPIRES || 0;
    if (window.__FAPI_JWT && expiresAt > now + REFRESH_BEFORE_EXPIRY_SEC) {
        return window.__FAPI_JWT;
    }
    return fetchAndUpdateFapiToken();
}

/**
 * Schedule a proactive refresh 5 minutes before expiry.
 * Clears any existing timer. Call after receiving a JWT (page load or fetch).
 */
export function scheduleProactiveJwtRefresh() {
    if (refreshTimerId !== null) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
    }
    const expiresAt = typeof window !== 'undefined' ? window.__FAPI_JWT_EXPIRES : 0;
    if (!expiresAt) return;
    const now = Math.floor(Date.now() / 1000);
    const refreshAt = expiresAt - REFRESH_BEFORE_EXPIRY_SEC;
    const delayMs = Math.max(0, (refreshAt - now) * 1000);
    if (delayMs <= 0) {
        fetchAndUpdateFapiToken();
        return;
    }
    refreshTimerId = setTimeout(() => {
        refreshTimerId = null;
        fetchAndUpdateFapiToken();
    }, delayMs);
}

// On load: bootstrap token state automatically.
// - If token is missing/expired/near-expiry, fetch immediately.
// - If token is healthy, schedule proactive refresh.
if (typeof window !== 'undefined') {
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = window.__FAPI_JWT_EXPIRES || 0;
    const hasUsableToken = Boolean(window.__FAPI_JWT) && expiresAt > (now + REFRESH_BEFORE_EXPIRY_SEC);
    if (hasUsableToken) {
        scheduleProactiveJwtRefresh();
    } else {
        fetchAndUpdateFapiToken();
    }
}

// When tab becomes visible: refresh token if expired/near-expiry (timers are throttled when hidden)
if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const expiresAt = typeof window !== 'undefined' ? window.__FAPI_JWT_EXPIRES : 0;
        if (!expiresAt) return;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt <= now + REFRESH_BEFORE_EXPIRY_SEC) {
            fetchAndUpdateFapiToken();
        }
    });
}

//# debugId=19488bbd-760b-5230-a182-79e86dd21278
